<script lang="ts" setup>
import { computed, onMounted, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { formatIncompletePhoneNumber } from 'libphonenumber-js';
import dayjs from 'dayjs';
import {
  IconCheckboxActive as ActiveIcon,
  IconCheckboxDefault as DefaultIcon,
} from '@profeat/ui-kit';

import { Contact, ContactField, SystemFieldType } from '~/entities/contacts';
import { useContacts } from '~/stores/contacts.store';

const props = defineProps<{
  contacts: Contact[];
  headerFields: ContactField[];
}>();

const { t } = useI18n();
const contactsStore = useContacts();

const lastCheckedIndex = ref<number | null>(null);
const notUpdate = ref(false);
const checkElemRef = ref();
const statusField = computed(() => {
  return props.headerFields.find(
    ({ type, name }) => type === SystemFieldType.Enum && name === 'status',
  )!;
});

const getFieldValue = (contactId: string, fieldId: string) => {
  return contactsStore.getFieldValue({ fieldId, contactId });
};
const removeTag = async (contactTagId: string, contactId: string) => {
  await contactsStore.removeTag({ contactTagId, contactId });
};
const getValue = (contactId: string, fieldId: string) => {
  const fieldValue = getFieldValue(contactId, fieldId);

  if (fieldValue) {
    const { type, name } =
      'sysField' in fieldValue ? fieldValue.sysField : fieldValue.customField;
    let value = fieldValue.value;

    if (type === 'date') {
      const date = dayjs(value).format('DD.MM.YYYY');
      value = date === 'Invalid Date' ? value : date;
    }
    if (['int', 'number'].includes(type)) {
      const replacedValue = value.replaceAll(',', '.');
      const isFraction = replacedValue.split('.').length > 1;

      value = isFraction ? Number(replacedValue).toFixed(2) : replacedValue;
    }
    if (type === 'phone') value = formatIncompletePhoneNumber(`+${value}`);
    if (type === 'string' && name === 'tg_nick') value = `@${value}`;
    if (type === 'enum' && name === 'status')
      value = t(`customers.statuses.${value}`);
    if (type === 'enum' && name === 'upload_method')
      value = t(`customers.uploadMethods.${value}`);

    return value;
  }

  const isSentAt = contactsStore.getSystemFields.find(
    ({ id, name }) => id === fieldId && name === 'sended_at',
  );
  if (isSentAt) return '—';

  return '';
};
const handleCheck = (event: MouseEvent, index: number, customerId: string) => {
  const target = event.target as HTMLInputElement;
  const checked = target.checked;
  const withShiftKey = event.shiftKey;

  if (withShiftKey && lastCheckedIndex.value !== null) {
    const start = Math.min(lastCheckedIndex.value, index);
    const end = Math.max(lastCheckedIndex.value, index);

    for (let i = start; i <= end; i++) {
      contactsStore.selections[props.contacts[i].id] = checked;
    }
  }

  lastCheckedIndex.value = index;
  contactsStore.selections[customerId] = checked;

  if (!checked && contactsStore.isAllSelected) {
    contactsStore.isAllSelected = false;
    notUpdate.value = true;
    contactsStore.selectionMode = 'all';
  }
  if (checked && !contactsStore.isAllSelected) {
    const isAllChecked = Object.values(contactsStore.selections).every(Boolean);

    if (isAllChecked) {
      contactsStore.isAllSelected = true;
      notUpdate.value = true;
      contactsStore.selectionMode = 'all';
    }
  }
};
const getIcon = (customerId: string) => {
  return contactsStore.selections[customerId] ? ActiveIcon : DefaultIcon;
};
const isPositiveStatus = (contactId: string, fieldId: string): boolean => {
  const status = getFieldValue(contactId, fieldId);

  return (
    fieldId === statusField.value?.id && ['active', 'new'].includes(status.value)
  );
};
const isLostStatus = (contactId: string, fieldId: string): boolean => {
  const status = getFieldValue(contactId, fieldId);

  return fieldId === statusField.value?.id && status.value === 'lost';
};
const isArchiveStatus = (contactId: string, fieldId: string): boolean => {
  const status = getFieldValue(contactId, fieldId);

  return fieldId === statusField.value?.id && status.value === 'archive';
};

const checkWidthText = (text: string) => {
  const testElement: HTMLDivElement = checkElemRef.value
  if(!testElement) return
  testElement.textContent = text
  const textWidth = testElement.offsetWidth
  testElement.textContent = ''
  return textWidth > 218 ? true : false
}

watch(
  () => contactsStore.isAllSelected,
  (newValue) => {
    if (!notUpdate.value) {
      for (const customerId in contactsStore.selections) {
        if (contactsStore.selections[customerId] !== newValue) {
          contactsStore.selections[customerId] = newValue;
        }
      }
    }

    notUpdate.value = false;
  },
);
</script>

<template>
   <div ref="checkElemRef" class="check-container" ></div>
  <tr
    v-for="(contact, index) in contacts"
    :key="contact.id"
  >
    <td>
      <div class="body-cell check">
        <label class="checkbox">
          <input
            v-model="contactsStore.selections[contact.id]"
            @click="handleCheck($event, index, contact.id)"
            type="checkbox"
            class="input-checkbox"
          />

          <component :is="getIcon(contact.id)" class="icon" />
        </label>
      </div>
    </td>

    <td
      v-for="field in headerFields"
      :key="contact.id + field.id"
      :class="{ 'archive': isArchiveStatus(contact.id, field.id) }"
    >
      <div v-if="field.type === 'tag'" class="body-cell">
        <TagViewComponent
          :tags="contact.tags"
          @on-delete="(contactTagId) => removeTag(contactTagId, contact.id)"
        />
      </div>
      <div v-else
        :class="[
          'body-cell',
          {
            'active': isPositiveStatus(contact.id, field.id),
            'lost': isLostStatus(contact.id, field.id),
          },
        ]"
      >
        <PHint placement="top" v-if="checkWidthText(getValue(contact.id, field.id))">
          <template #activator="{ setReference, onHover, outHover }">
            <div
              class="hint-target"
              :ref="setReference"
              @mouseover="onHover"
              @mouseleave="outHover"
            >
              {{ getValue(contact.id, field.id) }}
          </div>
          </template>
          <template #hint>
            <div class="hint-value"> {{ getValue(contact.id, field.id) }}</div>
          </template>
        </PHint>
          <span v-else> {{ getValue(contact.id, field.id) }}</span>
      </div>
    </td>
  </tr>
</template>

<style lang="scss" scoped>
.check-container{
  padding: 16px;
  font-size: 14px;
  font-weight: 400;
  line-height: 143%;
  white-space: nowrap;
  visibility: hidden;
  position: absolute;
}
tr {
  td{
    border-bottom: 1px solid var(--common-smog, #e5ebf0);

  }
  .body-cell {
    padding: 16px;
    user-select: none;
    white-space: nowrap;
    max-width: 250px;
    .hint-target{
      text-overflow: ellipsis;
      white-space: nowrap;
      overflow: hidden;
      max-width: 250px;
    }
    :deep(.hint){
      white-space: wrap;
      overflow: visible;
      max-width: 400px;
      .hint-value{
        max-width: 400px;
      }
    }
    
    
    span {
      color: var(--common-black, #2B3238);
      font-size: 14px;
      font-weight: 400;
      line-height: 143%;
    }
  }
  .check {
    padding: 16px 8px 16px 16px;
    width: 48px;
  }
  .active {
    span {
      color: var(--additional-green, #51B77A);
    }
  }
  .lost {
    span {
      color: var(--additional-error, #E46060);
    }
  }

  .checkbox {
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;

    .input-checkbox {
      display: none;
      appearance: none;
      cursor: pointer;

      & + .icon {
        color: var(--common-rain, #D0DBE4);
      }
      &:checked + .icon {
        color: var(--common-black, #2B3238);
      }
    }
  }
}

.archive {
  opacity: .3;
}
</style>
